/**
 * Утилита для показа индикаторов загрузки
 */

/**
 * Создаёт менеджер индикаторов загрузки
 */
export function createLoadingIndicatorManager({ documentRef = document } = {}) {
  const activeLoaders = new Map();
  let loaderCounter = 0;

  /**
   * Показывает индикатор загрузки
   * @param {string} key - Уникальный ключ для идентификации загрузки
   * @param {Object} options - Опции
   * @param {string} options.message - Сообщение для показа
   * @param {HTMLElement} options.target - Элемент, на котором показывать индикатор
   * @param {string} options.type - Тип индикатора: 'spinner', 'skeleton', 'overlay'
   * @returns {string} ID загрузки
   */
  function showLoading(key, options = {}) {
    const {
      message = 'Загрузка...',
      target = null,
      type = 'spinner'
    } = options;

    const loaderId = `loader-${++loaderCounter}`;
    
    let loaderElement;
    
    switch (type) {
      case 'spinner':
        loaderElement = createSpinnerLoader(message);
        break;
      case 'skeleton':
        loaderElement = createSkeletonLoader(target);
        break;
      case 'overlay':
        loaderElement = createOverlayLoader(message);
        break;
      default:
        loaderElement = createSpinnerLoader(message);
    }

    if (target && type !== 'overlay') {
      // Показываем индикатор внутри целевого элемента
      target.style.position = 'relative';
      target.appendChild(loaderElement);
    } else {
      // Показываем overlay поверх всего
      documentRef.body.appendChild(loaderElement);
    }

    activeLoaders.set(key, {
      id: loaderId,
      element: loaderElement,
      target,
      type
    });

    return loaderId;
  }

  /**
   * Скрывает индикатор загрузки
   * @param {string} key - Ключ загрузки
   */
  function hideLoading(key) {
    const loader = activeLoaders.get(key);
    if (!loader) return;

    if (loader.element && loader.element.parentNode) {
      loader.element.parentNode.removeChild(loader.element);
    }

    activeLoaders.delete(key);
  }

  /**
   * Создаёт спиннер загрузки
   */
  function createSpinnerLoader(message) {
    const container = documentRef.createElement('div');
    container.className = 'loading-indicator-spinner';
    container.style.cssText = `
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 12px;
      padding: 20px;
      background: rgba(0, 0, 0, 0.7);
      border-radius: 12px;
      z-index: 10000;
    `;

    const spinner = documentRef.createElement('div');
    spinner.className = 'loading-spinner';
    spinner.style.cssText = `
      width: 32px;
      height: 32px;
      border: 3px solid rgba(77, 255, 253, 0.3);
      border-top-color: #4dfffd;
      border-radius: 50%;
      animation: spin 0.8s linear infinite;
    `;

    const text = documentRef.createElement('div');
    text.textContent = message;
    text.style.cssText = `
      color: #ffffff;
      font-size: 14px;
      font-weight: 500;
    `;

    container.appendChild(spinner);
    container.appendChild(text);

    // Добавляем анимацию, если её ещё нет
    if (!documentRef.getElementById('loading-spinner-style')) {
      const style = documentRef.createElement('style');
      style.id = 'loading-spinner-style';
      style.textContent = `
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `;
      documentRef.head.appendChild(style);
    }

    return container;
  }

  /**
   * Создаёт skeleton loader
   */
  function createSkeletonLoader(target) {
    if (!target) return null;

    const skeleton = documentRef.createElement('div');
    skeleton.className = 'loading-skeleton';
    skeleton.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(
        90deg,
        rgba(255, 255, 255, 0.05) 0%,
        rgba(255, 255, 255, 0.1) 50%,
        rgba(255, 255, 255, 0.05) 100%
      );
      background-size: 200% 100%;
      animation: skeleton-loading 1.5s ease-in-out infinite;
      border-radius: inherit;
      z-index: 10;
    `;

    // Добавляем анимацию, если её ещё нет
    if (!documentRef.getElementById('skeleton-style')) {
      const style = documentRef.createElement('style');
      style.id = 'skeleton-style';
      style.textContent = `
        @keyframes skeleton-loading {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `;
      documentRef.head.appendChild(style);
    }

    return skeleton;
  }

  /**
   * Создаёт overlay загрузки
   */
  function createOverlayLoader(message) {
    const overlay = documentRef.createElement('div');
    overlay.className = 'loading-overlay';
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.8);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 99999;
    `;

    const content = createSpinnerLoader(message);
    overlay.appendChild(content);

    return overlay;
  }

  /**
   * Обёртка для async функции с автоматическим показом загрузки
   */
  async function withLoading(key, asyncFn, options = {}) {
    showLoading(key, options);
    try {
      const result = await asyncFn();
      return result;
    } finally {
      hideLoading(key);
    }
  }

  return {
    showLoading,
    hideLoading,
    withLoading
  };
}








